import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import { MAX_COMMAND_UNITS } from '../net/protocol';
import type { RuleSet } from '../rules';
import {
  bonusCity,
  type Cell,
  type Command,
  type EntityId,
  fromMilli,
  isMilitary,
  type PlayerId,
  type SimState,
  type Unit,
  unitMaxHp,
  unitRank,
} from '../sim';
import { cityName, ownCities } from './cities';
import { ownGroups } from './groups';
import { labelOrDefault, type Names } from './names';

/**
 * Send troops: the player picks some of their military (groups, idle units
 * by city, units stored in a barracks or factory) and sends them at something a
 * notification pointed out (a rival found, or an attacker). Pure: no React,
 * so it's unit-testable.
 */

/** What troops are sent at: a rival unit or building (`id`), or just a tile. */
export interface SendTarget {
  id?: EntityId;
  /** Where it was when the notification was raised (its last known tile). */
  pos: Cell;
  /** "p2's Warrior", for the picker. */
  label: string;
}

/** One pickable row of the picker. */
export interface TroopRow {
  key: string;
  label: string;
  /** "2 Warrior, 1 Archer · 50/60 hp". */
  detail: string;
  /** Highest rank among its units. */
  rank: number;
  unitIds: EntityId[];
}

export interface TroopOptions {
  groups: TroopRow[];
  /** Military not in a group, stored or busy, by nearest own city. */
  idle: TroopRow[];
  /** Units stored in each barracks or factory. */
  stored: TroopRow[];
}

const idNumber = (id: string) => Number.parseInt(id.slice(1), 10);

function hexDistance(a: Cell, b: Cell): number {
  return Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.q + a.r - b.q - b.r));
}

function row(rules: RuleSet, key: string, label: string, units: Unit[]): TroopRow {
  const counts = new Map<string, number>();
  for (const u of units) counts.set(u.type, (counts.get(u.type) ?? 0) + 1);
  const types = [...counts]
    .map(([type, n]) => `${n} ${rules.units[type]?.name ?? type}`)
    .join(', ');
  const hp = units.reduce((sum, u) => sum + u.hp, 0);
  const maxHp = units.reduce((sum, u) => sum + unitMaxHp(rules, u), 0);
  return {
    key,
    label,
    detail: `${types} · ${Math.ceil(fromMilli(hp))}/${Math.ceil(fromMilli(maxHp))} hp`,
    rank: Math.max(0, ...units.map((u) => unitRank(rules, u))),
    unitIds: units.map((u) => u.id),
  };
}

/** The player's military the picker offers, by groups, idle by city and stored per barracks or factory. */
export function troopOptions(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  /** The player's own group and city names (client-local), else "Group 2" / "City 1". */
  names?: Names
): TroopOptions {
  const military = Object.values(state.units)
    .filter((u) => u.owner === playerId && isMilitary(rules, u.type))
    .sort((a, b) => idNumber(a.id) - idNumber(b.id));

  const groups: TroopRow[] = [];
  for (const g of ownGroups(state, playerId)) {
    const units = g.units.filter((u) => isMilitary(rules, u.type));
    if (units.length > 0)
      groups.push(row(rules, `g:${g.id}`, labelOrDefault(names, g.id, `Group ${g.number}`), units));
  }

  const cities = ownCities(state, rules, playerId).sort((a, b) => idNumber(a.id) - idNumber(b.id));
  const idleByCity = new Map<string, Unit[]>();
  for (const u of military) {
    if (u.inside || u.groupId !== undefined || u.order || (u.path && u.path.length > 0)) continue;
    let city = '';
    let best = Number.POSITIVE_INFINITY;
    for (const c of cities) {
      const d = hexDistance(u.pos, c.pos);
      if (d < best) {
        best = d;
        city = c.id;
      }
    }
    const list = idleByCity.get(city);
    if (list) list.push(u);
    else idleByCity.set(city, [u]);
  }
  const idle = [...idleByCity].map(([cityId, units]) => {
    const city = state.buildings[cityId];
    const label = city
      ? `Idle near ${cityName(city, playerId, cityNumbers, undefined, names)}`
      : 'Idle';
    return row(rules, `i:${cityId}`, label, units);
  });

  const byBarracks = new Map<string, Unit[]>();
  for (const u of military) {
    if (!u.inside) continue;
    const list = byBarracks.get(u.inside);
    if (list) list.push(u);
    else byBarracks.set(u.inside, [u]);
  }
  const stored = [...byBarracks]
    .sort(([a], [b]) => idNumber(a) - idNumber(b))
    .map(([barracksId, units]) => {
      const b = state.buildings[barracksId];
      const name = b ? (rules.buildings[b.type]?.name ?? b.type) : 'Barracks';
      const city = b ? bonusCity(state, rules, b) : undefined;
      const label = city
        ? `${name} · ${cityName(city, playerId, cityNumbers, undefined, names)}`
        : name;
      return row(rules, `s:${barracksId}`, label, units);
    });

  return { groups, idle, stored };
}

/**
 * The tile to send troops to: a rival unit's live tile while the player sees
 * it, else where it was last known (`target.pos`), so nothing leaks through
 * the fog. Buildings don't move.
 */
export function targetCell(
  state: SimState,
  target: SendTarget,
  visible: (key: HexTileMapKey) => boolean
): Cell {
  const unit = target.id ? state.units[target.id] : undefined;
  if (unit && !unit.inside && visible(getHexTileMapKey(unit.pos))) return { ...unit.pos };
  return { ...target.pos };
}

/**
 * Commands sending the player's `unitIds` at `target`: `attack` orders (the
 * sim deploys stored units first and, for a rival unit it can't see, makes it
 * a move to the given last known tile), or a move to the tile when there's no
 * entity to attack. Split to stay under the wire's unit cap.
 */
export function sendCommands(
  state: SimState,
  playerId: PlayerId,
  unitIds: readonly EntityId[],
  target: SendTarget,
  visible: (key: HexTileMapKey) => boolean
): Command[] {
  const own = [...new Set(unitIds)].filter((id) => state.units[id]?.owner === playerId);
  const cell = targetCell(state, target, visible);
  const commands: Command[] = [];
  for (let i = 0; i < own.length; i += MAX_COMMAND_UNITS) {
    const chunk = own.slice(i, i + MAX_COMMAND_UNITS);
    commands.push(
      target.id
        ? { type: 'attack', unitIds: chunk, targetId: target.id, target: cell }
        : { type: 'move', unitIds: chunk, target: cell }
    );
  }
  return commands;
}

/** Troops sent at a target, tracked until the attack resolves (map ring). */
export interface SentAttack {
  id: number;
  target: SendTarget;
  /** The tile they were sent to. */
  cell: Cell;
  unitIds: EntityId[];
  /** Sim tick it was sent. */
  tick: number;
  /** Seen under way at least once (the command has landed). */
  started?: boolean;
}

/** Ticks to wait for a sent order to show up in the state before giving up on it. */
const SEND_GRACE_TICKS = 50;
/** A sent unit still walking to within this of the tile counts as under way. */
const ARRIVE_RADIUS = 3;

/** Whether any of the sent units is still attacking the target or on the way. */
function underWay(state: SimState, sent: SentAttack): boolean {
  return sent.unitIds.some((id) => {
    const u = state.units[id];
    if (!u) return false;
    if (u.order?.kind === 'attack' && u.order.targetId === sent.target.id) return true;
    const end = u.path?.at(-1);
    return end !== undefined && hexDistance(end, sent.cell) <= ARRIVE_RADIUS;
  });
}

/**
 * Updates the tracked attacks for the current state: one under way is marked
 * started; one that has started and is no longer under way (target down,
 * units arrived, dead or re-ordered), or that never started within the grace
 * period, is dropped. Returns the same array when nothing changed.
 */
export function updateSentAttacks(state: SimState, list: readonly SentAttack[]): SentAttack[] {
  let changed = false;
  const next: SentAttack[] = [];
  for (const sent of list) {
    const active = underWay(state, sent);
    if (active) {
      if (!sent.started) changed = true;
      next.push(sent.started ? sent : { ...sent, started: true });
    } else if (!sent.started && state.tick <= sent.tick + SEND_GRACE_TICKS) {
      next.push(sent);
    } else {
      changed = true;
    }
  }
  return changed ? next : (list as SentAttack[]);
}

/** Where to draw a tracked attack's ring: on the target while it's seen, else the tile sent to. */
export function sentAttackPos(
  state: SimState,
  sent: SentAttack,
  visible: (key: HexTileMapKey) => boolean
): Cell {
  const building = sent.target.id ? state.buildings[sent.target.id] : undefined;
  if (building && visible(getHexTileMapKey(building.pos))) return building.pos;
  const unit = sent.target.id ? state.units[sent.target.id] : undefined;
  if (unit && !unit.inside && visible(getHexTileMapKey(unit.pos))) return unit.pos;
  return sent.cell;
}
